import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  TextField, 
  Button, 
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { passwordResetService } from '../services/passwordResetService';
import '../styles/pages/_esqueci-senha.scss';

const RedefinirSenha: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ 
    password?: string; 
    confirmPassword?: string; 
  }>({});

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      validateToken(tokenFromUrl);
    } else {
      setIsValidating(false);
      setIsTokenValid(false);
    }
  }, [searchParams]);

  const validateToken = async (token: string) => {
    try {
      const isValid = await passwordResetService.validateToken(token);
      setIsTokenValid(isValid);
    } catch (error) {
      setIsTokenValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  const validatePassword = (password: string): boolean => {
    return password.length >= 8; // Backend exige mínimo 8 caracteres
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): boolean => {
    return password === confirmPassword;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({});

    // Validate password
    if (!validatePassword(newPassword)) {
      setErrors({ password: 'A senha deve ter pelo menos 8 caracteres' });
      return;
    }

    // Validate confirm password
    if (!validateConfirmPassword(newPassword, confirmPassword)) {
      setErrors({ confirmPassword: 'As senhas não coincidem' });
      return;
    }

    setIsLoading(true);

    try {
      await passwordResetService.confirmReset(token, newPassword);
      toast.success('Senha redefinida com sucesso!');
      navigate('/inicio/login');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Erro ao redefinir senha. Tente novamente.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPassword(e.target.value);
    if (errors.password) {
      setErrors({ ...errors, password: undefined });
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) {
      setErrors({ ...errors, confirmPassword: undefined });
    }
  };

  if (isValidating) {
    return (
      <Box className="esqueci-senha-page" sx={{ textAlign: 'center', maxWidth: 400, mx: 'auto' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Validando token...
        </Typography>
      </Box>
    );
  }

  if (!isTokenValid) {
    return (
      <Box className="esqueci-senha-page" sx={{ textAlign: 'center', maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
          Token Inválido
        </Typography>
        
        <Alert severity="error" sx={{ mb: 3 }}>
          O link de recuperação de senha é inválido ou expirou.
          Solicite um novo link de recuperação.
        </Alert>

        <Button
          variant="contained"
          onClick={() => navigate('/inicio/esqueci-senha')}
          sx={{ mr: 2 }}
        >
          Solicitar Novo Link
        </Button>

        <Button
          variant="text"
          onClick={() => navigate('/inicio/login')}
        >
          Voltar ao Login
        </Button>
      </Box>
    );
  }

  return (
    <Box className="esqueci-senha-page" sx={{ maxWidth: 400, mx: 'auto' }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 1, textAlign: 'center' }}>
        Redefinir Senha
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
        Digite sua nova senha
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Nova Senha"
          type={showPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={handlePasswordChange}
          error={!!errors.password}
          helperText={errors.password}
          disabled={isLoading}
          sx={{ mb: 3 }}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          fullWidth
          label="Confirmar Nova Senha"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={handleConfirmPasswordChange}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword}
          disabled={isLoading}
          sx={{ mb: 3 }}
          autoComplete="new-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          sx={{ mb: 2, py: 1.5 }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Redefinir Senha'
          )}
        </Button>
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Button
          variant="text"
          onClick={() => navigate('/inicio/login')}
          disabled={isLoading}
        >
          Cancelar
        </Button>
      </Box>
    </Box>
  );
};

export default RedefinirSenha;
